import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { contactService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { 
  MessageSquare, User, Phone, Mail, Save, Trash2, Eye, Reply, 
  Clock, CheckCircle, AlertCircle, Settings, RefreshCw 
} from 'lucide-react';

const AdminMessages = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  
  // Advisor form
  const [advisorForm, setAdvisorForm] = useState({
    firstName: '',
    lastName: '',
    phoneDirecte: '',
    phoneFix: '',
    email: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [messagesData, advisorData] = await Promise.all([
        contactService.getAllMessages(),
        contactService.getAdvisor()
      ]);
      setMessages(messagesData);
      setAdvisor(advisorData);
      setAdvisorForm({
        firstName: advisorData?.firstName || '',
        lastName: advisorData?.lastName || '',
        phoneDirecte: advisorData?.phoneDirecte || '',
        phoneFix: advisorData?.phoneFix || '01 46 39 39 39',
        email: advisorData?.email || 'contact@younited-credit.fr'
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAdvisor = async () => {
    if (!advisorForm.firstName.trim() || !advisorForm.lastName.trim()) {
      toast({
        title: 'Erreur',
        description: 'Le nom et prénom sont obligatoires',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const updated = await contactService.updateAdvisor(advisorForm);
      setAdvisor(updated);
      toast({
        title: 'Succès',
        description: 'Informations du conseiller mises à jour'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    setReplyText(message.adminReply || '');
    setIsMessageDialogOpen(true);
    
    // Mark as read if unread
    if (message.status === 'unread') {
      try {
        await contactService.markAsRead(message.id);
        setMessages(messages.map(m => 
          m.id === message.id ? { ...m, status: 'read' } : m
        ));
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast({
        title: 'Erreur',
        description: 'Veuillez écrire une réponse',
        variant: 'destructive'
      });
      return;
    }

    setReplying(true);
    try {
      const updated = await contactService.replyToMessage(selectedMessage.id, replyText);
      setMessages(messages.map(m => 
        m.id === selectedMessage.id ? updated : m
      ));
      setSelectedMessage(updated);
      toast({
        title: 'Succès',
        description: 'Réponse envoyée au client'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: "Impossible d'envoyer la réponse",
        variant: 'destructive'
      });
    } finally {
      setReplying(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;

    try {
      await contactService.deleteMessage(messageId);
      setMessages(messages.filter(m => m.id !== messageId));
      setIsMessageDialogOpen(false);
      toast({
        title: 'Succès',
        description: 'Message supprimé'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'replied':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 flex items-center"><CheckCircle className="h-3 w-3 mr-1" />Répondu</span>;
      case 'read':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 flex items-center"><Eye className="h-3 w-3 mr-1" />Lu</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 flex items-center"><AlertCircle className="h-3 w-3 mr-1" />Non lu</span>;
    }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messages & Contact</h1>
          <p className="text-gray-500 mt-1">Gérez les messages clients et les informations du conseiller</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total messages</p>
              <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Non lus</p>
              <p className="text-2xl font-bold text-yellow-600">{unreadCount}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Répondus</p>
              <p className="text-2xl font-bold text-green-600">
                {messages.filter(m => m.status === 'replied').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Advisor Settings */}
        <Card className="p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Settings className="h-5 w-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Informations conseiller</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Prénom</Label>
              <Input
                value={advisorForm.firstName}
                onChange={(e) => setAdvisorForm({ ...advisorForm, firstName: e.target.value })}
                placeholder="Prénom du conseiller"
              />
            </div>

            <div>
              <Label className="font-semibold">Nom</Label>
              <Input
                value={advisorForm.lastName}
                onChange={(e) => setAdvisorForm({ ...advisorForm, lastName: e.target.value })}
                placeholder="Nom du conseiller"
              />
            </div>

            <div>
              <Label className="font-semibold">Téléphone direct</Label>
              <Input
                value={advisorForm.phoneDirecte}
                onChange={(e) => setAdvisorForm({ ...advisorForm, phoneDirecte: e.target.value })}
                placeholder="01 23 45 67 89"
              />
            </div>

            {/* Phone Fix and Email (now editable) */}
            <div className="pt-4 border-t border-gray-200 space-y-4">
              <p className="text-xs text-gray-500 mb-3">Informations de contact</p>
              
              <div>
                <Label className="text-sm text-gray-700 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  Tél. fixe
                </Label>
                <Input
                  value={advisorForm.phoneFix}
                  onChange={(e) => setAdvisorForm({ ...advisorForm, phoneFix: e.target.value })}
                  placeholder="01 46 39 39 39"
                />
              </div>
              
              <div>
                <Label className="text-sm text-gray-700 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  Email
                </Label>
                <Input
                  type="email"
                  value={advisorForm.email}
                  onChange={(e) => setAdvisorForm({ ...advisorForm, email: e.target.value })}
                  placeholder="contact@younited-credit.fr"
                />
              </div>
            </div>

            <Button 
              onClick={handleSaveAdvisor} 
              disabled={saving}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              {saving ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Enregistrement...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Enregistrer</>
              )}
            </Button>
          </div>
        </Card>

        {/* Messages List */}
        <Card className="p-6 border border-gray-200 lg:col-span-2">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Messages des clients</h2>
            {unreadCount > 0 && (
              <span className="px-2 py-1 text-xs rounded-full bg-red-500 text-white">
                {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}
              </span>
            )}
          </div>

          {messages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun message pour le moment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Client</TableHead>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.map((msg) => (
                    <TableRow 
                      key={msg.id} 
                      className={msg.status === 'unread' ? 'bg-yellow-50' : ''}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{msg.clientName}</p>
                          <p className="text-xs text-gray-500">{msg.clientEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{msg.subject}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(msg.createdAt).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>{getStatusBadge(msg.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openMessage(msg)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Voir
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteMessage(msg.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Message Detail Dialog */}
      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-2 text-purple-600" />
              Message de {selectedMessage?.clientName}
            </DialogTitle>
          </DialogHeader>

          {selectedMessage && (
            <div className="space-y-4">
              {/* Message Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Client</p>
                  <p className="font-medium">{selectedMessage.clientName}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{selectedMessage.clientEmail}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{new Date(selectedMessage.createdAt).toLocaleString('fr-FR')}</p>
                </div>
                <div>
                  <p className="text-gray-500">Statut</p>
                  {getStatusBadge(selectedMessage.status)}
                </div>
              </div>

              {/* Subject */}
              <div>
                <p className="text-gray-500 text-sm">Sujet</p>
                <p className="font-semibold text-gray-900">{selectedMessage.subject}</p>
              </div>

              {/* Message */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>

              {/* Reply Section */}
              <div className="border-t pt-4">
                <Label className="font-semibold">Répondre au client</Label>
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Écrivez votre réponse..."
                  rows={4}
                  className="mt-2"
                />
                
                <div className="flex justify-end space-x-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsMessageDialogOpen(false)}
                  >
                    Fermer
                  </Button>
                  <Button
                    onClick={handleReply}
                    disabled={replying}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {replying ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Envoi...</>
                    ) : (
                      <><Reply className="h-4 w-4 mr-2" />Envoyer la réponse</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Previous Reply */}
              {selectedMessage.adminReply && (
                <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                  <p className="text-xs text-green-700 font-semibold mb-1">
                    <CheckCircle className="h-3 w-3 inline mr-1" />
                    Réponse envoyée le {new Date(selectedMessage.repliedAt).toLocaleString('fr-FR')}
                  </p>
                  <p className="text-sm text-green-800">{selectedMessage.adminReply}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMessages;
